import {Grid, LinearProgress, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';

import FilterBar from '../../components/FilterBar/FilterBar';
import PlotLineChart from '../../components/LineChart/LineChart';
import PlotPieChart from '../../components/PieChart/PieChart';
import ToolBar from '../../components/ToolBar/ToolBar';
import {ConfigConstant} from '../../constants/config';
import {getOperationsReport, getPerformanceReport} from '../../service/OpsRaw';
import {getDateFromText, getDefaultFilters} from '../../utils/helper';

const getChannelName = (channelCode) => {
  const channels = ConfigConstant.MONGO_CONFIG?.OD_CHANNELS;
  return Object.keys(channels).filter((key) => channels[key] === channelCode)[0];
};

const getExportData = (rawData) => {
  const channels = new Set();
  rawData.forEach((entry) => {
    entry.channels.forEach((channel) => {
      if (channel.channelCode !== 'NA')
        channels.add(`${getChannelName(channel.channelGroupCode)}-${getChannelName(channel.channelCode)}`);
    });
  });
  const headers = ['Date', ...channels];
  let csvData = headers.join(',') + '\n';
  rawData.forEach((entry) => {
    const row = [];
    row.push(entry.date);
    channels.forEach((channel) => {
      const channelData = entry.channels.find(
        (d) => `${getChannelName(d.channelGroupCode)}-${getChannelName(d.channelCode)}` === channel
      );
      channelData
        ? row.push(`${((channelData.failedCount / channelData.totalCount) * 100).toFixed(2)} %`)
        : row.push('');
    });
    csvData += row.join(',') + '\n';
  });
  return csvData;
};

const getGroupByChannelChartData = (channelData, operationType) => {
  const data = [];
  let totalCount = 0;
  let totalFailedCount = 0;
  if (Array.isArray(channelData) && channelData.length) {
    channelData.forEach((d) => {
      const index = data.push({
        day: format(new Date(d.date), 'dd MMM'),
      });
      if (Array.isArray(d?.channels) && d.channels.length) {
        d.channels.forEach((channel) => {
          if (channel?.channelCode === channel?.channelGroupCode) {
            data[index - 1][`${getChannelName(channel?.channelCode)}-direct`] = channel?.failedCount;
            data[index - 1][`${getChannelName(channel?.channelCode)}-direct-percent`] = `${(
              (channel?.failedCount / channel?.totalCount) *
              100
            ).toFixed(2)}`;
          } else {
            data[index - 1][`${getChannelName(channel?.channelGroupCode)}-${getChannelName(channel?.channelCode)}`] =
              channel?.failedCount;
            data[index - 1][
              `${getChannelName(channel?.channelGroupCode)}-${getChannelName(channel?.channelCode)}-percent`
            ] = `${((channel?.failedCount / channel?.totalCount) * 100).toFixed(2)}`;
          }
          totalFailedCount += channel?.failedCount;
          totalCount += channel?.totalCount;
        });
      }
    });
  }
  return {
    data,
    XAxisKey: 'day',
    dataKeys: ConfigConstant.MONGO_CONFIG?.CHANNEL_COLOR_CODES,
    percentage: totalCount ? ((totalFailedCount / totalCount) * 100).toFixed(2) : 0,
    exportData: {
      title: `${operationType}Report`,
      data: getExportData(channelData),
    },
  };
};

const getThrottleData = (theme, throttleData) => {
  const data = [];
  let totalCount = 0;
  if (Array.isArray(throttleData) && throttleData.length) {
    throttleData?.forEach((topic, index) => {
      data.push({
        fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[index],
        key: `${topic.channelName}`,
        value: topic.count,
      });
      totalCount += topic.count;
    });
  }
  return {data, totalCount};
};

const getIndex = (data, category) => {
  for (let i = 0; i < Object.keys(data).length; i++) {
    if (Object.keys(data)[i] === category) {
      return data[Object.keys(data)[i]];
    }
  }
  return null;
};
const getLagByTopicChartData = (topicData) => {
  const data = [];
  const indexData = {};
  let totalCount = 0;
  if (Array.isArray(topicData) && topicData.length) {
    for (let i = 0; i < topicData.length; i++) {
      let index;
      if (ConfigConstant.MONGO_CONFIG?.TIMERANGE_CATEGORY_TO_IGNORE?.includes(topicData[i].category)) {
        continue;
      } else if (getIndex(indexData, topicData[i].category)) {
        index = getIndex(indexData, topicData[i].category);
      } else {
        index = data.push({
          category: topicData[i].category,
        });
        indexData[topicData[i].category] = index;
      }
      if (topicData[i].channelCode === topicData[i].channelGroupCode) {
        data[index - 1][`${topicData[i].channelName}-direct`] = topicData[i]?.count;
      } else {
        data[index - 1][`${topicData[i].channelGroupName}-${topicData[i].channelName}`] = topicData[i]?.count;
      }
      totalCount += topicData[i].count;
    }
  }
  return {
    data,
    XAxisKey: 'category',
    dataKeys: ConfigConstant.MONGO_CONFIG?.CHANNEL_COLOR_CODES,
    totalCount,
  };
};

const getLagData = (theme, lagData) => {
  const processingTimeData = {
    data: [],
    totalCount: 0,
  };
  const categories = {};
  if (Array.isArray(lagData) && lagData.length) {
    lagData?.forEach((lag) => {
      if (!ConfigConstant.MONGO_CONFIG?.TIMERANGE_CATEGORY_TO_IGNORE.includes(lag.category)) {
        categories[lag.category] = (categories[lag.category] || 0) + lag.count;
      }
    });
  }
  let i = 0;
  Object.keys(categories).forEach((category) => {
    processingTimeData.data.push({
      fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[i],
      key: category,
      value: categories[category],
    });
    i += 1;
    processingTimeData.totalCount += categories[category];
  });
  return processingTimeData;
};

const OperationsReport = () => {
  const theme = useTheme();

  const commonFilters = useSelector((store) => store.commonFilters);

  const defaultFilters = getDefaultFilters(window?.location?.search, commonFilters);

  const [filters, setFilters] = useState([
    {
      id: 'Date',
      displayValue: defaultFilters?.date?.displayValue || 'Today',
      value: defaultFilters?.date?.value || getDateFromText('Today'),
      type: 'date',
      selectType: 'single',
      key: 'date',
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'Operation',
      type: 'menu',
      selectType: 'single',
      key: 'operationType',
      displayValue: defaultFilters?.operationType?.displayValue || ['Menu Push'],
      value: defaultFilters?.operationType?.value || ['MENU_PUSH'],
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'City',
      type: 'menu',
      selectType: 'multiple',
      key: 'city',
      displayValue: defaultFilters?.city?.displayValue || [],
      value: defaultFilters?.city?.value || [],
      isApplied: Array.isArray(defaultFilters?.city?.value) && defaultFilters?.city?.value.length > 0 ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Country',
      type: 'menu',
      selectType: 'multiple',
      key: 'countryCode',
      displayValue: defaultFilters?.countryCode?.displayValue || [],
      value: defaultFilters?.countryCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.countryCode?.value) && defaultFilters?.countryCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Channel',
      type: 'menu',
      selectType: 'multiple',
      key: 'channelCode',
      displayValue: defaultFilters?.channelCode?.displayValue || [],
      value: defaultFilters?.channelCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.channelCode?.value) && defaultFilters?.channelCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Brand',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerBrandCode',
      displayValue: defaultFilters?.partnerBrandCode?.displayValue || [],
      value: defaultFilters?.partnerBrandCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerBrandCode?.value) && defaultFilters?.partnerBrandCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
  ]);

  const dataQueryOperations = useQuery({
    queryKey: ['data-operationsReport'],
    queryFn: () => getOperationsReport({filters}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const dataQueryPerformance = useQuery({
    queryKey: ['data-performanceReport'],
    queryFn: () => getPerformanceReport({filters}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [operationData, setOperationChartData] = useState({});
  const [hubThrottleData, setThrottleData] = useState({});
  const [hubLagData, setHubLagData] = useState({});
  const [hubTopicLagData, setHubTopicLagData] = useState({});

  useEffect(() => {
    if (dataQueryOperations.isSuccess) {
      const operationFilter = filters.find((filter) => filter.id === 'Operation');
      const operationType = operationFilter ? operationFilter.value[0] : 'MENU_PUSH';
      const groupByChannelOperationChartData = getGroupByChannelChartData(dataQueryOperations.data.data, operationType);
      setOperationChartData(groupByChannelOperationChartData);
    }
  }, [dataQueryOperations.data]);

  useEffect(() => {
    if (dataQueryPerformance.isSuccess) {
      const throttleChartData = getThrottleData(theme, dataQueryPerformance.data?.data?.throttleData);
      setThrottleData(throttleChartData);
      const lagChartData = getLagData(theme, dataQueryPerformance.data?.data?.lagData);
      setHubLagData(lagChartData);
      const topicLagChartData = getLagByTopicChartData(dataQueryPerformance.data?.data?.lagData);
      setHubTopicLagData(topicLagChartData);
    }
  }, [dataQueryPerformance.data]);

  return (
    <>
      <ToolBar
        headerText={'Operations Reports'}
        showGlobalSearch={false}
        showLegends={false}
        showExportButton={{button: false, tooltip: false}}
      />
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        showApplyActionButton={true}
        showFilterActionButton={true}
        dataQuery={[dataQueryOperations, dataQueryPerformance]}
        allowDateRangeSelection={false}
        shouldValidateMultiOpsSelection={false}
      />
      {dataQueryOperations.isFetching || dataQueryOperations.isLoading ? (
        <LinearProgress />
      ) : dataQueryOperations.isError ? (
        <Typography>Failed to fetch Operations reports!</Typography>
      ) : (
        <>
          <PlotLineChart chartData={operationData} titleInfo={{text: 'Failures:', xPercent: '10%'}} />
          <br />
        </>
      )}
      {dataQueryPerformance.isFetching || dataQueryPerformance.isLoading ? (
        <Typography>Be Patient. Fetching Performance reports!</Typography>
      ) : dataQueryPerformance.isError ? (
        <Typography>Failed to fetch Performance reports!</Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <PlotPieChart
                chartData={hubLagData}
                innerRadius={60}
                outerRadius={100}
                titleInfo={{text: 'Request Processing Time:', xPercent: '52%'}}
              />
            </Grid>
            <Grid item xs={6}>
              <PlotPieChart
                chartData={hubThrottleData}
                innerRadius={60}
                outerRadius={100}
                titleInfo={{text: 'ThrottleWatch:', xPercent: '31%'}}
              />
            </Grid>
          </Grid>
          <br />
          <PlotLineChart chartData={hubTopicLagData} titleInfo={{text: 'Topic Processing Time:', xPercent: '23%'}} />
        </>
      )}
    </>
  );
};

export default OperationsReport;
